import { LitElement, html, css } from 'lit';
import { msg,str } from '@lit/localize';
import { accessibility,grid, formStyles } from '../../utilities/styles';
import { arrowBack } from '../../utilities/icons';
import { AllInSessionStorage } from '../Utilities/AllInSessionStorage';
import { AllInConditionals } from '../Utilities/AllInConditionals';
import { AllInOrganisationConsumer } from '../Utilities/AllInOrganisationConsumer';
import { addIcon } from '../../utilities/icons';
import { handle403s } from '../../utilities/AllInErrorHandling';


export class AllInOrganisationSubscriptionsGroup extends AllInConditionals(AllInSessionStorage(AllInOrganisationConsumer(LitElement))) {
    static properties = {
        name: {type:String},
        loading: {type:Boolean, state:true},
        groupProfiles: {type:Array, state:true},
        _formResponse: {type:String, state:true},
        _showAddScreen: {type:Boolean, state:true},
    }

    constructor(){
        super();
    }

    async connectedCallback(){
        super.connectedCallback();
        let groupProfiles = this._getSessionStorageItem(`allin_org_${this.currentOrg}_group_profiles`);
        if(groupProfiles){
            this.groupProfiles = JSON.parse(groupProfiles);
        }
        if(this.groupProfiles){
            this._fetchGroupProfiles();
        }
        else{
            await this._fetchGroupProfiles();
        }
    }

    render(){
        let main = ''
        let button = html`<button class="back" @click=${this._hidePanel}><span aria-hidden="true">${arrowBack}</span>${msg(str`Return to subscriptions`)}</button>`;
        let cta = html`<div class="cta">Read more</div>`;
        if(this.loading){
            main = html`<allin-loader loading></allin-loader>`;
        }
        else{
            if(this._showAddScreen){
                main = html`<h3>${msg(str`Add an organisation to the group`)}</h3>
                <form class="bg" @submit=${this._handleAddOrg}>
                    <div class="message ${this._formResponse?'error':''}" aria-live="polite">${this._formResponse}</div>
                    <allin-organisation-search name="addOrgSearch" resturl="${this.restUrl}" org_id="${this.currentOrg}" nonce="${this.nonce}"></allin-organisation-search>
                </form>
                <button class="cancel" @click=${()=>this._showAddScreen = false}><span aria-hidden="true">${arrowBack}</span>${msg(str`Back to group`)}</button>
                `;

            }
            else if(!this.groupProfiles){
                main = html`
                    <h3>${msg(str`Organisations in this group`)}</h3>
                    <div class="bg">
                        ${msg(str`You are not part of a group. If you would like to be a lead organisation to make the payments, click below to add your associate organisations. If you are not a lead organisation, please wait for the lead organisation to send you an invite.`)}
                        <button class="cta" @click=${()=>this._showAddScreen = true}>${msg(str`Add organisation`)}</button>
                    </div>
                `;
            }
            else{
                let associatedOrgs = this.groupProfiles.associatedOrganisations;
                let isLead = this.currentOrg === this.groupProfiles.id;
                let canAdd = isLead&&associatedOrgs.find(org=>org.invitedStatus === 'Accepted')
                let button = '';
                let _30daysFromNow = new Date();
                _30daysFromNow.setDate(_30daysFromNow.getDate() + 40);
                if(this.organisation?.subscriptionTier=='Tier1'&&canAdd){
                        button = html`<a href="${this.hardlinks.organisation_dashboard_add_subscription}?org_id=${this.currentOrg}&group=true" class="button">Add subscription</a>`;
                }
                else if(this.organisation?.subscriptionEndDate && new Date(this.organisation.subscriptionEndDate) < _30daysFromNow && canAdd){
                    button = html`<a href="${this.hardlinks.organisation_dashboard_add_subscription}?org_id=${this.currentOrg}&group=true" class="button">Renew subscription</a>`;
                }
                main = html`
                    <h3>${msg(str`Organisations in this group`)}</h3>
                    <ul title="${msg(`Organisations in this group`)}">
                        <li>
                            <div class="content">
                                <p><strong>${msg(`Name`)}</strong></p>
                                <p>${this.groupProfiles.name}</p>
                            </div>
                            <div class="content">
                                <p><strong>${msg(`Organisation role`)}</strong></p>
                                <p>${msg('Leading organisation')}${isLead?' (You)':''}</p>
                            </div>
                        </li>
                        ${associatedOrgs.map((org)=>{
                            //console.log('org',org); 
                            let button = '';
                            if(isLead && org.invitedStatus === 'Accepted'){
                                button = html`<button @click=${()=>this._removeOrg(org.organisationGroupId)}>${msg(str`Remove organisation`)}</button>`;
                            }
                            else if(isLead && org.invitedStatus === 'Invited'){
                                button = html`<p class="invite-status invited">${msg(str`Invitation pending`)}</p>`;
                            }
                            else if(isLead && org.invitedStatus === 'Rejected'){
                                button = html`<p class="invite-status error">${msg(str`Invitation rejected`)}</p>`;
                            }
                            return html`
                                <li>
                                    <div class="content">
                                        <p><strong>${msg(`Name`)}</strong></p>
                                        <p>${org.name}</p>
                                    </div>
                                    <div class="content">
                                       <p><strong>${msg(`Organisation role`)}</strong></p>
                                        <p>${msg('Associate organisation')}${this.currentOrg === org.id?' (You)':''}</p>
                                    </div>
                                    <div class="buttons">
                                        ${button}
                                    </div>
                                </li>
                            `;})}
                            ${isLead?html`<button @click=${()=>this._showAddScreen = true}><span aria-hidden="true">${addIcon}</span>${msg(`Invite new organisation`)}</button>`:''}
                    </ul>
                    ${button}
                `;
            }
        }
        return html`
            <div class="group-container">
                ${button}
                <h2>${msg(str`Group subscriptions`)}</h2>
                ${main}
            </div>
        `;
    }

    _hidePanel(e){
        e.preventDefault();
       this._updateContext({panel:''});
    }

    async _handleAddOrg(e){
        e.preventDefault();
        this.loading = true;
        const input = e.target.querySelector('input');
        this._formResponse = '';
        const orgId = input.value;
        let response = await fetch(`${this.restUrl}`,{
            method:'POST',
            headers: {
                'Content-Type': 'application/json',
                'AllIn-Nonce': this.nonce,
            },
            body: JSON.stringify({'action':`${this.action}-add-group`,'org_to_add': orgId,'org_id':this.currentOrg}),
        });

        if(response.status === 403){
            handle403s();
            return;
        }

        let data = await response.json();
        if(data.ok){
            this._showAddScreen = false;
            await this._fetchGroupProfiles();
            this.loading = false;
        }
        else{
            this.loading = false;
            this._formResponse = data.message;
        }
    }

    async _fetchGroupProfiles(){
        this.loading = true;
        let url = `${this.restUrl}?org_id=${this.currentOrg}&action=${this.action}-group&nonce=${this.nonce}`;
        try{
            let response = await fetch(url,{
                headers: {
                    'AllIn-Nonce': this.nonce,
                }
            })

            if(response.status === 403){
                handle403s();
                return;
            }

            let data = await response.json();
            this.groupProfiles = data.group;
            if(this.groupProfiles){
                this._setSessionStorageItem(`allin_org_${this.currentOrg}_group_profiles`,JSON.stringify(this.groupProfiles));
            }
        }
        catch(error){
            console.error('Error:', error);
        };
        this.loading = false;
    }

    async _removeOrg(orgId){
        this.loading = true;
        let response = await fetch(`${this.restUrl}`,{
            method:'POST',
            headers: {
                'Content-Type': 'application/json',
                'AllIn-Nonce': this.nonce,
            },
            body: JSON.stringify({'action':`${this.action}-remove-group`,'org_to_remove': orgId,'org_id':this.currentOrg}),
        });

        if(response.status === 403){
            handle403s();
            return;
        }

        let data = await response.json();
        if(data.ok){
            this._showAddScreen = false;
            await this._fetchGroupProfiles();
            this.loading = false;
        }
        else{
            this.loading = false;
            this._formResponse = data.message;
        }
    }

    static styles = [
        accessibility,
        grid,
        formStyles,
        css`
        .group-container{
            margin-bottom:2rem;
        }
        button.back{
            color:var(--allin-highlight-color,#472961);
            background-color:transparent;
            border:0;
            padding:0;
            font-weight:700;
            display:flex;
            align-items:center;
            gap:0.5rem;
            font-weight:var(--allin-highlight-weight,700);
            cursor:pointer;
        }
        button.back span{
            display:flex;
            align-items:center;
            justify-content:center;
            width:1rem;
        }
        button.back svg{
            transform:rotate(0);
            transition:transform var(--transition) ease;
            width:1rem;
        }
        button.back:hover{
            text-decoration:underline;
        }

        button.back:hover svg{
            transform:rotate(15deg);
        }
        @media (prefers-reduced-motion: reduce) {
            button.back svg{
                transform:none !important;
            }
        }
        .cta{
            margin-top:1rem;
        }
        .bg{
            padding:var(--allin-padding,2rem);
            background-color:var(--allin-bg-color,#ccc);
            margin-bottom:0.5rem;
        }

         allin-organisation-search .bg{
            background-color:var(--color-bg);
        }
        allin-organisation-search ul{
            list-style:none;
            padding:0;
            margin:1rem 0;
        }
        allin-organisation-search ul li{
            margin-bottom:0.5rem;
            display:grid;
            grid-template-columns: 3rem 1fr auto;
            gap:1rem;
            align-items:center;
            padding-left: 1rem;
            img{
                width:3rem;
                height:3rem;
                object-fit:cover;
            }
            button{
                height:fit-content;
            }
        }
        allin-organisation-search ul li img{
            width:3rem;
            height:3rem;
            object-fit:cover;
        }
        allin-organisation-search ul li button{
            height:fit-content;
        }
        .searchboxes {
            display: grid;
            grid-template-columns: 1fr auto;
            width: 100%;
            max-width: 800px;
            margin-bottom: 2rem;
            position:relative;
        }

        .searchbox {
            position: relative;
            label {
                margin-bottom: 0.5rem;
            }
        }
        .searchbox svg {
            position: absolute;
            left: 0.5rem;
            bottom: 0.8rem;
            width: 1.5rem;
            height: 1.5rem;
        }

        .searchbox input{
            padding-left:2.5rem;
            border-radius:0.5rem 0 0 0.5rem;
        }
        .searchboxes button{
            border-radius:0 0.5rem 0.5rem 0;
            height:calc(3rem + 2px);
            align-self:end;
        }

         ul{
            list-style:none;
            padding:0;
            margin:0;
            background-color:var(--allin-white-color,#fff);
            border:1px solid var(--allin-border-color,#000);
            padding:2rem;
            margin-bottom:2rem;
        }

        ul li{
            display:grid;
            grid-template-columns:4fr 3fr 3fr;
            font-size:0.9rem;
        }

        ul+ul{
            margin-top:2rem;
        }

        .content p{
            margin-bottom:0.5rem;
        }

        .content p:has(>strong){
            margin-bottom:0.1rem;
        }

        ul li:first-of-type{
            padding-top: 1.5rem;
        }
        ul li:last-of-type{
            padding-bottom: 0.5rem;
        }
        ul li + li{
            border-top:1px solid var(--allin-border-color,#000);
            padding-top:1.5rem;
            margin-top:1rem
        }

        ul button,.cancel{
            color:var(--allin-highlight-color,#472961);
            background-color:transparent;
            height:fit-content;
            padding:0;
            font-weight:700;
            text-decoration:underline;
            font-size:0.9rem;
            display:flex;
            gap:0.5rem;
            align-items:center;
            padding:0;
            text-align:left;
        }

        ul button span:not(.resend, .resent), .cancel span{
            display:flex;
            align-items:center;
            width:1.2rem;
        }

        ul button svg, .cancel svg{
            transition:transform var(--transition) ease;
            width:1.2rem;
        }

        ul button:hover, .cancel:hover{
            text-decoration-thickness:2px;
            background-color:transparent;
            color:var(--allin-highlight-color,#472961);
            font-weight:700;
        }

        ul button:hover svg, .cancel:hover svg{
            transform:rotate(5deg);
        }

        li:has(+button){
            border-bottom:1px solid var(--allin-border-color,#000);
            padding-bottom:1.5rem;
            margin-bottom:1rem
        }

        li a{
            color:var(--allin-text-color,#000);
            word-break:break-all;
            
        }

        li a:hover{
            text-decoration-thickness:1.2px;
        }

        li>*nth-child(2){
           padding:0 1rem;
        }

        li>*:nth-child(3){
            justify-self:end;
            padding-right:1rem;
        }
        .invite-status{
            font-weight:700;
        }

        .invite-status.invited{
            color:var(--allin-disable-color,#707070);
        }
        `
    ]
}